import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Tabs,
  Tab,
  useTheme,
  Slide,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  Close,
  Assessment,
  Timeline,
  TrendingUp,
  Home,
} from '@mui/icons-material';
import type { HomeIntelligenceData } from '../../../../shared/intelligence/types.js';
import IntelligenceScoreCard from '../IntelligenceScoreCard';
import QuickInsightsPanel from '../QuickInsightsPanel';
import MarketAnalyticsSection from '../MarketAnalyticsSection';
import PropertyAssessmentCard from '../PropertyAssessmentCard';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface IntelligenceModalProps {
  open: boolean;
  onClose: () => void;
  section: 'score' | 'insights' | 'market' | 'assessment';
  intelligence: HomeIntelligenceData | null;
  loading?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`intelligence-tabpanel-${index}`}
      aria-labelledby={`intelligence-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `intelligence-tab-${index}`,
    'aria-controls': `intelligence-tabpanel-${index}`,
  };
};

export const IntelligenceModal: React.FC<IntelligenceModalProps> = ({
  open,
  onClose,
  section,
  intelligence,
  loading = false,
}) => {
  const theme = useTheme();

  // Map section to tab index
  const sectionToTabIndex = {
    score: 0,
    insights: 1,
    market: 2,
    assessment: 3,
  };

  const [tabValue, setTabValue] = useState(sectionToTabIndex[section]);

  // Update tab when section prop changes
  React.useEffect(() => {
    setTabValue(sectionToTabIndex[section]);
  }, [section]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const tabData = [
    {
      label: 'Score',
      icon: <Assessment />,
      component: (
        <IntelligenceScoreCard
          score={intelligence?.propertyScore || {
            overall: 0,
            marketPosition: 0,
            propertyCondition: 0,
            investmentPotential: 0,
            riskFactors: 0,
            marketMomentum: 0,
          }}
          loading={loading}
          compact={false}
        />
      ),
    },
    {
      label: 'Insights',
      icon: <Timeline />,
      component: (
        <QuickInsightsPanel
          insights={intelligence?.quickInsights || []}
          loading={loading}
          maxInsights={6}
        />
      ),
    },
    {
      label: 'Market',
      icon: <TrendingUp />,
      component: (
        <MarketAnalyticsSection
          analytics={intelligence?.marketAnalytics || null}
          loading={loading}
          showComparisons={true}
        />
      ),
    },
    {
      label: 'Assessment',
      icon: <Home />,
      component: (
        <PropertyAssessmentCard
          assessment={intelligence?.propertyAssessment || null}
          loading={loading}
        />
      ),
    },
  ];

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      <AppBar
        sx={{
          position: 'relative',
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: 'none',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <Close />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Property Intelligence
          </Typography>
        </Toolbar>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            backgroundColor: theme.palette.background.paper,
            minHeight: 48,
            '& .MuiTab-root': {
              minHeight: 48,
              fontSize: '0.875rem',
            },
          }}
        >
          {tabData.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              label={tab.label}
              iconPosition="start"
              sx={{
                flexDirection: 'row',
                '& .MuiTab-iconWrapper': {
                  marginRight: 1,
                  marginBottom: 0,
                },
              }}
              {...a11yProps(index)}
            />
          ))}
        </Tabs>
      </AppBar>

      <DialogContent
        sx={{
          p: 0,
          backgroundColor: theme.palette.background.default,
          overflow: 'auto',
        }}
      >
        {tabData.map((tab, index) => (
          <TabPanel key={index} value={tabValue} index={index}>
            {tab.component}
          </TabPanel>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default IntelligenceModal;